import { IPage } from "../../widgets/interfaces/model/index";
import { createReducers } from "../util";
import {
	calculateGroupExtents,
	pageDummyAction,
	scaleImageToFrame,
	setCurvePoints,
	setGeometry,
	setHandlePoints,
	setShapeTextContent,
} from "./PageOperations";
import {
	addAttributeToPlaceholder,
	addImageToPlaceholder,
	addPage,
	addPageObject,
	addPageObjects,
	alignObjects,
	applyFormatPainter,
	applyTextToSelectedShape,
	changePageObjectOrder,
	changeShapeType,
	deleteKeys,
	deleteObjects,
	deletePage,
	deleteShapeById,
	distributeObjects,
	editPlaceholder,
	flipObjects,
	group,
	lockObjects,
	makeShapeCustom,
	paste,
	removeMigratedExtents,
	resetPlaceholderExtends,
	rotateObjects,
	scaleCroppedImage,
	scaleObjectExtends,
	scaleObjects,
	setLineCap,
	setLineColor,
	setLineDash,
	setLineType,
	setObjectExtends,
	setObjectsFill,
	setObjectsShape,
	setOffsets,
	setPageMeta,
	setPages,
	setShapeAdjustValues,
	setShapeAutosizeValues,
	setSrcRectRefExt,
	setStrokeWidth,
	sortPages,
	translateCroppedImage,
	translateObjects,
	togglePlaceholderAutospace,
	togglePlaceholderLabels,
	togglePlaceholderLabelsOptionally,
	ungroup,
} from "./PageOperations";

/**
 * Default state for the reducer
 */
const initialState: IPage[] = [];

/**
 * Action constants for page operations
 */
export enum PageActionConstants {
    DUMMY_ACTION = "DUMMY_ACTION",
    SET_PAGES = "SET_PAGES",
    ADD_PAGE = "ADD_PAGE",
    ADJUST_GROUP_EXTENTS = "ADJUST_GROUP_EXTENTS",
    SET_PAGE_META = "SET_PAGE_META",
    ADD_ATTRIBUTE_TO_PLACEHOLDER = "ADD_ATTRIBUTE_TO_PLACEHOLDER",
    ADD_IMAGE_TO_PLACEHOLDER = "ADD_IMAGE_TO_PLACEHOLDER",
    ADD_PAGE_OBJECT = "ADD_PAGE_OBJECT",
    ADD_PAGE_OBJECTS = "ADD_PAGE_OBJECTS",
    Set_SHAPE_ADJUST_VALUES = "Set_SHAPE_ADJUST_VALUES",
    SET_SHAPE_AUTOSIZE_VALUES = "SET_SHAPE_AUTOSIZE_VALUES",
    DELETE_PAGE = "DELETE_PAGE",
    DELETE_OBJECTS = "DELETE_OBJECTS",
    DELETE_KEYS = "DELETE_KEYS",
    SET_FILL = "SET_FILL",
    SET_SHAPE = "SET_SHAPE",
    TRANSLATE_CROPPED_IMAGE = "TRANSLATE_CROPPED_IMAGE",
    TRANSLATE_OBJECTS= "TRANSLATE_OBJECTS",
    GROUP = "GROUP",
    SET_LINE_COLOR = "SET_LINE_COLOR",
    SET_LINE_TYPE = "SET_LINE_TYPE",
    SET_STROKE_WIDTH = "SET_STROKE_WIDTH",
    ROTATE_OBJECT = "ROTATE_OBJECT",
    SET_LINE_DASH = "SET_LINE_DASH",
    FLIP_OBJECTS = "FLIP_OBJECTS",
    ALIGN_OBJECTS = "ALIGN_OBJECTS",
    LOCK_OBJECTS = "LOCK_OBJECTS",
    UNGROUP = "UNGROUP",
    DISTRIBUTE_OBJECTS = "DISTRIBUTE_OBJECTS",
    PASTE = "PASTE",
    SCALE = "SCALE",
    SCALE_CROPPED_IMAGE = "SCALE_CROPPED_IMAGE",
    CHANGE_ORDER = "CHANGE_ORDER",
    APPLY_FORMAT_PAINTER= "APPLY_FORMAT_PAINTER",
    SET_CURVE_POINTS = "SET_CURVE_POINTS",
    SET_GEOMETRY = "SET_GEOMETRY",
    SET_HANDLE_POINTS = "SET_HANDLE_POINTS",
    SET_OFFSET = "SET_OFFSET",
    SET_TEXT_CONTENT = "SET_TEXT_CONTENT",
    SET_LINE_CAP = "SET_LINE_CAP",
    CHANGE_SHAPE_TYPE = "CHANGE_SHAPE_TYPE",
    MAKE_SHAPE_CUSTOM = "MAKE_SHAPE_CUSTOM",
    SCALE_EXTENDS = "SCALE_EXTENDS",
    SCALE_IMAGE_TO_FRAME = "SCALE_IMAGE_TO_FRAME",
    DELETE_SHAPE_BY_ID = "DELETE_SHAPE_BY_ID",
    SORT_PAGES = "SORT_PAGES",
    APPLY_TEXT_STYLE_TO_SELECTED_SHAPE = "APPLY_TEXT_STYLE_TO_SELECTED_SHAPE",
    SET_OBJECT_EXTENDS = "SET_OBJECT_EXTENDS",
    RESET_PLACEHOLDER_EXTENDS = "RESET_PLACEHOLDER_EXTENDS",
    EDIT_PLACEHOLDER = "EDIT_PLACEHOLDER",
    SET_SRC_RECT_REF_EXT = "SET_SRC_RECT_REF_EXT",
    REMOVE_MIGRATED_EXTENTS = "REMOVE_MIGRATED_EXTENTS",
    TOGGLE_PLACEHOLDER_AUTOSPACE = "TOGGLE_PLACEHOLDER_AUTOSPACE",
    TOGGLE_PLACEHOLDER_LABELS = "TOGGLE_PLACEHOLDER_LABELS",
    TOGGLE_PLACEHOLDER_LABELS_OPTIONALLY = "TOGGLE_PLACEHOLDER_LABELS_OPTIONALLY",
}

/**
 * Page reducers
 */
export const pages = createReducers(initialState, {
	[PageActionConstants.DUMMY_ACTION]: pageDummyAction,
	[PageActionConstants.SET_PAGES]: setPages,
	[PageActionConstants.ADD_PAGE]: addPage,
	[PageActionConstants.ADJUST_GROUP_EXTENTS]: calculateGroupExtents,
	[PageActionConstants.SET_PAGE_META]: setPageMeta,
	[PageActionConstants.ADD_ATTRIBUTE_TO_PLACEHOLDER]: addAttributeToPlaceholder,
	[PageActionConstants.ADD_IMAGE_TO_PLACEHOLDER]: addImageToPlaceholder,
	[PageActionConstants.ADD_PAGE_OBJECT]: addPageObject,
	[PageActionConstants.ADD_PAGE_OBJECTS]: addPageObjects,
	[PageActionConstants.Set_SHAPE_ADJUST_VALUES]: setShapeAdjustValues,
	[PageActionConstants.SET_SHAPE_AUTOSIZE_VALUES]: setShapeAutosizeValues,
	[PageActionConstants.DELETE_PAGE]: deletePage,
	[PageActionConstants.DELETE_OBJECTS]: deleteObjects,
	[PageActionConstants.DELETE_KEYS]: deleteKeys,
	[PageActionConstants.TRANSLATE_OBJECTS]: translateObjects,
	[PageActionConstants.GROUP]: group,
	[PageActionConstants.LOCK_OBJECTS]: lockObjects,
	[PageActionConstants.UNGROUP]: ungroup,
	[PageActionConstants.SET_FILL]: setObjectsFill,
	[PageActionConstants.SET_SHAPE]: setObjectsShape,
	[PageActionConstants.SET_LINE_COLOR]: setLineColor,
	[PageActionConstants.SET_LINE_TYPE]: setLineType,
	[PageActionConstants.SET_STROKE_WIDTH]: setStrokeWidth,
	[PageActionConstants.ROTATE_OBJECT]: rotateObjects,
	[PageActionConstants.SET_LINE_DASH]: setLineDash,
	[PageActionConstants.FLIP_OBJECTS]: flipObjects,
	[PageActionConstants.ALIGN_OBJECTS]: alignObjects,
	[PageActionConstants.DISTRIBUTE_OBJECTS]: distributeObjects,
	[PageActionConstants.PASTE]: paste,
	[PageActionConstants.SCALE]: scaleObjects,
	[PageActionConstants.SCALE_CROPPED_IMAGE]: scaleCroppedImage,
	[PageActionConstants.CHANGE_ORDER]: changePageObjectOrder,
	[PageActionConstants.APPLY_FORMAT_PAINTER]: applyFormatPainter,
	[PageActionConstants.SET_CURVE_POINTS]: setCurvePoints,
	[PageActionConstants.SET_GEOMETRY]: setGeometry,
	[PageActionConstants.SET_HANDLE_POINTS]: setHandlePoints,
	[PageActionConstants.SET_OFFSET]: setOffsets,
	[PageActionConstants.SET_TEXT_CONTENT]: setShapeTextContent,
	[PageActionConstants.SET_LINE_CAP]: setLineCap,
	[PageActionConstants.CHANGE_SHAPE_TYPE]: changeShapeType,
	[PageActionConstants.MAKE_SHAPE_CUSTOM]: makeShapeCustom,
	[PageActionConstants.SCALE_EXTENDS]: scaleObjectExtends,
	[PageActionConstants.SCALE_IMAGE_TO_FRAME]: scaleImageToFrame,
	[PageActionConstants.DELETE_SHAPE_BY_ID]: deleteShapeById,
	[PageActionConstants.TRANSLATE_CROPPED_IMAGE]: translateCroppedImage,
	[PageActionConstants.APPLY_TEXT_STYLE_TO_SELECTED_SHAPE]: applyTextToSelectedShape,
	[PageActionConstants.SORT_PAGES]: sortPages,
	[PageActionConstants.SET_OBJECT_EXTENDS]: setObjectExtends,
	[PageActionConstants.RESET_PLACEHOLDER_EXTENDS]: resetPlaceholderExtends,
	[PageActionConstants.EDIT_PLACEHOLDER]: editPlaceholder,
	[PageActionConstants.SET_SRC_RECT_REF_EXT]: setSrcRectRefExt,
	[PageActionConstants.REMOVE_MIGRATED_EXTENTS]: removeMigratedExtents,
	[PageActionConstants.TOGGLE_PLACEHOLDER_AUTOSPACE]: togglePlaceholderAutospace,
	[PageActionConstants.TOGGLE_PLACEHOLDER_LABELS]: togglePlaceholderLabels,
	[PageActionConstants.TOGGLE_PLACEHOLDER_LABELS_OPTIONALLY]: togglePlaceholderLabelsOptionally,
});
